<script setup>
/**
 * PromotionCard Component
 * Displays a consolidated promotion card for multi-item promotions
 * Shows promotion image, name, item count, and navigates to promotion detail page
 */
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useCatalogStore } from '../../../app/store/catalog.js'
import { getPromoMakats, getPromoSteps } from '../../../core/utils/promoCalc.js'
import { ROUTES } from '../../../core/config/constants.js'

const props = defineProps({
  promotion: {
    type: Object,
    required: true,
  },
  itemsPerRow: {
    type: Number,
    default: 4,
  },
  isMobile: {
    type: Boolean,
    default: false,
  },
  isTablet: {
    type: Boolean,
    default: false,
  },
  clientMakat: {
    type: String,
    default: null,
  },
  isGridMode: {
    type: Boolean,
    default: false,
  },
})

const router = useRouter()
const route = useRoute()
const catalogStore = useCatalogStore()

// Get makats in promotion
const promoMakats = computed(() => {
  return getPromoMakats(props.promotion)
})

// Get items that actually exist in the client's catalog and are available for sale
const availableItems = computed(() => {
  return promoMakats.value
    .map((makat) => catalogStore.itemsByMakat.get(makat))
    .filter(Boolean)
    .filter((item) => item.status !== 'אסור למכירה')
})

// Get item count (filtered by catalog availability)
const itemCount = computed(() => {
  return availableItems.value.length
})

// Get ALL item images for slideshow (no limit)
const promoItemImages = computed(() => {
  const images = []
  for (const makat of promoMakats.value) {
    const item = catalogStore.itemsByMakat.get(makat)
    images.push(item?.image || null)
  }
  return images
})

// Slider ref and scroll functions
const sliderRef = ref(null)
const SCROLL_AMOUNT = 180 // ~3 icons worth

function scrollSlider(direction, event) {
  event.stopPropagation() // Prevent card click navigation
  if (!sliderRef.value) return
  const scrollAmount = direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT
  sliderRef.value.scrollBy({ left: scrollAmount, behavior: 'smooth' })
}

// Get promotion image (or first item's image as fallback)
const promoImage = computed(() => {
  // First try promotion's own image
  if (props.promotion.image) {
    return props.promotion.image
  }

  // Fallback to first item's image
  if (promoMakats.value.length > 0) {
    const firstMakat = promoMakats.value[0]
    const item = catalogStore.itemsByMakat.get(firstMakat)
    if (item?.image) {
      return item.image
    }
  }

  return null
})

// All displayable images for the slideshow (promo image + item images)
const slideshowImages = computed(() => {
  const images = []
  if (props.promotion.image) images.push(props.promotion.image)
  for (const makat of promoMakats.value) {
    const item = catalogStore.itemsByMakat.get(makat)
    if (item?.image && !images.includes(item.image)) {
      images.push(item.image)
    }
  }
  return images
})

// Crossfade slideshow state
const activeImageIndex = ref(0)
let slideshowTimer = null

function startSlideshow() {
  if (slideshowImages.value.length <= 1) return
  slideshowTimer = setInterval(() => {
    activeImageIndex.value = (activeImageIndex.value + 1) % slideshowImages.value.length
  }, 5000)
}

onMounted(() => {
  startSlideshow()
})

onBeforeUnmount(() => {
  if (slideshowTimer) clearInterval(slideshowTimer)
})

// Is first order promotion
const isFirstOrder = computed(() => {
  return props.promotion.first_order === true
})

const isWeekly = computed(() => {
  return !isFirstOrder.value && props.promotion.weeklyBenefit === true
})

// Days remaining until promotion ends
const daysRemaining = computed(() => {
  const endDate = props.promotion.date_end_date || props.promotion.end_date
  if (!endDate) return null
  const end = endDate.toDate ? endDate.toDate() : new Date(endDate)
  if (isNaN(end.getTime())) return null
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)
  return Math.ceil((end - now) / (1000 * 60 * 60 * 24))
})

// Badge text with countdown for expiring promos
const badgeText = computed(() => {
  if (isFirstOrder.value) return 'לקוחות חדשים'
  const kind = isWeekly.value ? 'הטבה שבועית' : 'מבצע'
  if (daysRemaining.value !== null && daysRemaining.value <= 1) return `יום אחרון! - ${kind}`
  if (daysRemaining.value !== null && daysRemaining.value < 10)
    return `נותרו ${daysRemaining.value} ימים - ${kind}`
  return kind
})

// Get regular price of the first item in the promotion
const firstItemPrice = computed(() => {
  const first = availableItems.value[0]
  if (!first?.price) return null
  return first.price.toFixed(2)
})

// Get promo step prices as range "X - Y"
const stepPrices = computed(() => {
  const steps = getPromoSteps(props.promotion)
  if (steps.length === 0) return null
  const prices = steps.map((s) => s.price)
  const min = Math.min(...prices)
  const max = Math.max(...prices)
  if (min === max) return '₪' + min.toFixed(2)
  return '₪' + min.toFixed(2) + ' - ' + '₪' + max.toFixed(2)
})

// Promo style class
const promoStyle = computed(() => {
  if (isFirstOrder.value) return 'first_order'
  return isWeekly.value ? 'weekly' : 'regular'
})

// Navigate to promotion detail page
function navigateToPromo() {
  // Check if in agent mode (clientMakat in route or prop)
  const clientMakat = props.clientMakat || route.params.clientMakat

  if (clientMakat) {
    router.push({
      name: ROUTES.AGENTS_PROMOTION_DETAIL,
      params: {
        clientMakat,
        promoId: props.promotion.id,
      },
    })
  } else {
    router.push({
      name: ROUTES.PROMOTION_DETAIL,
      params: {
        promoId: props.promotion.id,
      },
    })
  }
}

// Card classes based on layout mode and promo style
const cardClasses = computed(() => ({
  'promotion-card': true,
  'horizontal-layout': isHorizontal.value,
  'vertical-layout': !isHorizontal.value,
  'desktop-small': !props.isMobile && !props.isTablet && props.itemsPerRow <= 3,
  'desktop-large': !props.isMobile && !props.isTablet && props.itemsPerRow >= 4,
  'tablet-single': props.isTablet && props.itemsPerRow === 1,
  'tablet-double': props.isTablet && props.itemsPerRow === 2,
  'tablet-triple': props.isTablet && props.itemsPerRow >= 3,
  'mobile-single': props.isMobile && props.itemsPerRow === 1,
  'mobile-double': props.isMobile && props.itemsPerRow === 2,
  'promo-regular': promoStyle.value === 'regular',
  'promo-weekly': promoStyle.value === 'weekly',
  'promo-first-order': promoStyle.value === 'first_order',
  'grid-mode': props.isGridMode,
}))

// Determine if horizontal layout should be used
const isHorizontal = computed(() => {
  if (props.isMobile || props.isTablet) {
    return props.itemsPerRow === 1
  }
  return props.itemsPerRow <= 3
})
</script>

<template>
  <div :class="cardClasses" @click="navigateToPromo">
    <!-- Image Area -->
    <div class="card-image-wrapper">
      <!-- Item Count Badge -->
      <div class="item-count-badge">{{ itemCount }} פריטים</div>

      <!-- Crossfade Slideshow -->
      <template v-if="slideshowImages.length > 0">
        <img
          v-for="(img, idx) in slideshowImages"
          :key="idx"
          :src="img"
          :alt="promotion.label"
          class="card-image"
          :class="{ 'image-active': idx === activeImageIndex }"
          loading="lazy"
        />
      </template>
      <div v-else class="card-image-placeholder">
        <svg
          viewBox="0 0 24 24"
          width="48"
          height="48"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <path d="M12 8v4l3 3" />
          <circle cx="12" cy="12" r="9" />
        </svg>
        <span>מבצע</span>
      </div>

      <!-- Promo Type Bar (overlaid at bottom of image, under item-count-badge) -->
      <div v-if="isFirstOrder" class="promo-bar first-order">לקוחות חדשים</div>
      <div v-else-if="isWeekly" class="promo-bar weekly">{{ badgeText }}</div>
      <div v-else class="promo-bar regular">{{ badgeText }}</div>
    </div>

    <!-- Card Content -->
    <div class="card-content">
      <!-- Promotion Name -->
      <div class="promo-name">
        {{ promotion.label || 'מבצע' }}
      </div>

      <!-- Promotion Description (truncated) -->
      <div v-if="promotion.description" class="promo-description">
        {{ promotion.description }}
      </div>

      <!-- Regular Price of first item -->
      <div v-if="firstItemPrice" class="promo-regular-price">מחיר רגיל: ₪{{ firstItemPrice }}</div>

      <!-- Promo step prices -->
      <div v-if="stepPrices" class="promo-step-prices">מחירי מבצע: {{ stepPrices }}</div>

      <!-- Product Icons Slideshow -->
      <div v-if="promoItemImages.length > 0" class="promo-icons-wrapper">
        <!-- Left Arrow (desktop only) -->
        <button
          class="slider-arrow slider-arrow-left"
          aria-label="הקודם"
          @click="scrollSlider('left', $event)"
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>

        <!-- Scrollable Icons Container -->
        <div ref="sliderRef" class="promo-item-icons">
          <div v-for="(img, idx) in promoItemImages" :key="idx" class="promo-item-icon">
            <img v-if="img" :src="img" alt="" loading="lazy" />
            <svg
              v-else
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </div>
        </div>

        <!-- Right Arrow (desktop only) -->
        <button
          class="slider-arrow slider-arrow-right"
          aria-label="הבא"
          @click="scrollSlider('right', $event)"
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      </div>

      <!-- View Details Button -->
      <div class="view-details">
        <span>לחץ לצפייה בפריטים</span>
        <svg
          class="arrow-icon"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ============================================
   Base Card Styles
   ============================================ */
.promotion-card {
  position: relative;
  display: flex;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  transition: all var(--transition-fast);
  cursor: pointer;
}

/* Promotion gradient overlay (via ::before) */
.promotion-card::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 1;
  pointer-events: none;
  z-index: 0;
  transition: opacity var(--transition-fast);
}

/* Regular promotion - clean style */
.promotion-card.promo-regular::before {
  background: none;
}

/* First order (new customers) promotion - premium gold gradient */
.promotion-card.promo-first-order::before {
  background:
    radial-gradient(ellipse at top right, rgba(250, 204, 21, 0.22) 0%, transparent 50%),
    radial-gradient(ellipse at bottom left, rgba(202, 138, 4, 0.12) 0%, transparent 50%),
    linear-gradient(
      160deg,
      rgba(254, 249, 195, 0.25) 0%,
      rgba(253, 224, 71, 0.1) 30%,
      transparent 60%
    );
}

.promotion-card.promo-first-order {
  border-color: rgba(234, 179, 8, 0.35);
  box-shadow:
    0 0 0 1px rgba(234, 179, 8, 0.2),
    inset 0 1px 0 rgba(254, 249, 195, 0.5),
    0 4px 12px -2px rgba(234, 179, 8, 0.12);
}

.promotion-card:hover {
  border-color: var(--color-primary-light);
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.promotion-card:active {
  transform: scale(0.98);
}

/* ============================================
   Vertical Layout (Default - 4-5-6 per row)
   ============================================ */
.vertical-layout {
  flex-direction: column;
}

.vertical-layout .card-image-wrapper {
  width: 100%;
  flex-shrink: 0;
}

.vertical-layout .card-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.vertical-layout .view-details {
  margin-top: auto;
}

/* ============================================
   Horizontal Layout (2-3 per row desktop, 1 per row mobile)
   ============================================ */
.horizontal-layout {
  flex-direction: row-reverse;
}

.horizontal-layout .card-image-wrapper {
  width: 30%;
  flex-shrink: 0;
  height: 100%;
}

.horizontal-layout .card-content {
  width: 70%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

/* ============================================
   Image Wrapper
   ============================================ */
.card-image-wrapper {
  position: relative;
  z-index: 1;
  background: #ffffff;
  overflow: hidden;
}

/* First-order ("לקוחות חדשים" / new customers) promo:
   sunburst backdrop sits under the product image for an impressive look */
.promo-first-order .card-image-wrapper {
  background-image: url('../../../assets/card-backgrounds/02-sunburst-rays.svg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.card-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: var(--spacing-sm);
  opacity: 0;
  transition: opacity 0.6s ease;
}

.card-image.image-active {
  opacity: 1;
}

/* Image Placeholder */
.card-image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  gap: var(--spacing-sm);
  color: var(--text-muted);
  background: var(--bg-tertiary);
}

.card-image-placeholder svg {
  opacity: 0.5;
}

.card-image-placeholder span {
  font-size: 14px;
  font-weight: var(--font-weight-medium);
  opacity: 0.7;
}

/* Promo Type Bar (overlaid at bottom of image, under item-count-badge) */
.promo-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 6px 0;
  color: white;
  font-size: 12px;
  font-weight: var(--font-weight-bold);
  text-align: center;
  z-index: 2;
  letter-spacing: 0.5px;
}

.promo-bar.first-order {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.promo-bar.regular {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
}

.promo-bar.weekly {
  background: var(--color-info);
}

.promotion-card.promo-weekly {
  border-color: color-mix(in srgb, var(--color-info) 35%, transparent);
}

/* Item Count Badge (sits above promo-bar) */
.item-count-badge {
  position: absolute;
  bottom: 36px;
  right: var(--spacing-sm);
  padding: 4px 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 12px;
  font-weight: var(--font-weight-semibold);
  border-radius: var(--border-radius);
  z-index: 3;
}

/* ============================================
   Card Content
   ============================================ */
.card-content {
  position: relative;
  z-index: 1;
  padding: var(--spacing-sm) var(--spacing-md);
}

.promo-name {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  text-align: center;
  line-height: 1.3;
  margin-bottom: var(--spacing-xs);
}

.promo-description {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  text-align: center;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: var(--spacing-sm);
}

.promo-regular-price {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  text-align: center;
  margin-bottom: var(--spacing-xs);
}

.promo-step-prices {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: #dc2626;
  text-align: center;
  margin-bottom: var(--spacing-sm);
}

.promo-first-order .promo-step-prices {
  color: #b45309;
}

/* Product Icons Slideshow */
.promo-icons-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: var(--spacing-sm);
  position: relative;
}

.promo-item-icons {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  flex-wrap: nowrap;
  flex: 1;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.promo-item-icons::-webkit-scrollbar {
  display: none;
}

.promo-item-icon {
  height: 56px;
  width: auto;
  min-width: 56px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid rgba(239, 68, 68, 0.25);
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

.promo-first-order .promo-item-icon {
  border-color: rgba(245, 158, 11, 0.3);
}

.promo-item-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.promo-item-icon svg {
  color: var(--text-muted);
}

/* Slider Arrows */
.slider-arrow {
  display: none;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid var(--border-color);
  background: var(--bg-primary);
  color: var(--text-secondary);
  cursor: pointer;
  flex-shrink: 0;
  transition: all var(--transition-fast);
}

.slider-arrow:hover {
  background: var(--bg-secondary);
  border-color: var(--color-primary-light);
  color: var(--text-primary);
}

/* Show arrows only on desktop */
@media (min-width: 768px) {
  .slider-arrow {
    display: flex;
  }
}

.view-details {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--border-radius);
  color: #b45309;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  transition: all var(--transition-fast);
}

.promo-regular .view-details {
  color: #dc2626;
}

.promotion-card:hover .view-details {
  background: linear-gradient(135deg, #fca5a5 0%, #f87171 100%);
  color: #fff;
}

.arrow-icon {
  flex-shrink: 0;
  transition: transform var(--transition-fast);
}

.promotion-card:hover .arrow-icon {
  transform: translateX(-2px);
}

/* ============================================
   Desktop Sizes
   ============================================ */
.desktop-small {
  height: 220px;
}

.desktop-small .card-image-wrapper {
  height: 100%;
}

.desktop-small .promo-name {
  font-size: var(--font-size-xl);
  text-align: start;
}

.desktop-small .promo-description {
  text-align: start;
  -webkit-line-clamp: 1;
  margin-bottom: var(--spacing-xs);
}

.desktop-small .promo-regular-price,
.desktop-small .promo-step-prices {
  text-align: start;
  font-size: var(--font-size-xs);
}

.desktop-small .promo-icons-wrapper {
  justify-content: flex-start;
}

.desktop-small .view-details {
  justify-content: flex-start;
}

.desktop-large {
  min-height: 380px;
  height: auto;
}

.desktop-large .card-image-wrapper {
  height: 200px;
}

.desktop-large .card-content {
  padding: var(--spacing-md);
}

/* ============================================
   Tablet Sizes (768px - 1279px)
   ============================================ */
.tablet-single {
  height: 260px;
}

.tablet-single .card-image-wrapper {
  height: 100%;
}

.tablet-single .promo-name {
  font-size: var(--font-size-xl);
  text-align: start;
}

.tablet-single .promo-description {
  text-align: start;
  -webkit-line-clamp: 1;
  margin-bottom: var(--spacing-xs);
}

.tablet-single .promo-regular-price,
.tablet-single .promo-step-prices {
  text-align: start;
  font-size: var(--font-size-xs);
}

.tablet-single .promo-icons-wrapper {
  justify-content: flex-start;
}

.tablet-single .view-details {
  justify-content: flex-start;
}

.tablet-double {
  min-height: 420px;
  height: auto;
}

.tablet-double .card-image-wrapper {
  height: 200px;
}

/* Tablet 3 per row: vertical layout (matches desktop-large) */
.tablet-triple {
  min-height: 380px;
  height: auto;
}

.tablet-triple .card-image-wrapper {
  height: 200px;
}

.tablet-triple .card-content {
  padding: var(--spacing-md);
}

/* ============================================
   Mobile Single (1 per row) - Clean Horizontal
   ============================================ */
.mobile-single {
  height: 170px;
  border-radius: 12px;
}

.mobile-single .card-image-wrapper {
  height: 100%;
}

.mobile-single .promo-name {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  text-align: start;
  margin-bottom: 2px;
}

.mobile-single .promo-description {
  text-align: start;
  font-size: var(--font-size-xs);
  -webkit-line-clamp: 1;
  margin-bottom: 2px;
}

.mobile-single .promo-regular-price,
.mobile-single .promo-step-prices {
  text-align: start;
  font-size: 11px;
  margin-bottom: 1px;
}

/* Hide product icons on mobile - keep it clean */
.mobile-single .promo-icons-wrapper,
.mobile-double .promo-icons-wrapper {
  display: none;
}

.mobile-single .view-details {
  justify-content: flex-start;
  background: transparent;
  padding: 0;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
}

.mobile-single.promo-regular .view-details {
  color: #dc2626;
}

.mobile-single.promo-first-order .view-details {
  color: #b45309;
}

.mobile-single .slider-arrow {
  display: none;
}

/* ============================================
   Mobile Double (2 per row) - Clean Compact
   ============================================ */
.mobile-double {
  min-height: auto;
  height: auto;
  border-radius: 12px;
}

.mobile-double .card-image-wrapper {
  height: 170px;
}

.mobile-double .card-content {
  padding: var(--spacing-sm);
}

.mobile-double .promo-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  margin-bottom: 2px;
}

.mobile-double .promo-description {
  font-size: 11px;
  -webkit-line-clamp: 1;
  margin-bottom: var(--spacing-xs);
}

.mobile-double .view-details {
  font-size: var(--font-size-xs);
  padding: 6px var(--spacing-sm);
}

.mobile-double .slider-arrow {
  display: none;
}

/* ============================================
   Tablet & Mobile Hover/Active Adjustments
   ============================================ */
.tablet-single:hover,
.tablet-double:hover,
.tablet-triple:hover,
.mobile-single:hover,
.mobile-double:hover {
  transform: none;
  box-shadow: none;
}

.mobile-single:hover .view-details {
  background: transparent;
}

.mobile-single.promo-regular:hover .view-details {
  color: #dc2626;
}

.mobile-single.promo-first-order:hover .view-details {
  color: #b45309;
}

.mobile-double:hover .view-details {
  background: var(--bg-secondary);
  color: #b45309;
}

.tablet-single:active,
.tablet-double:active,
.tablet-triple:active,
.mobile-single:active,
.mobile-double:active {
  transform: scale(0.98);
  background: var(--bg-secondary);
}

/* ============================================
   Grid Mode (Square corners, no gaps)
   ============================================ */
.promotion-card.grid-mode {
  border-radius: 0 !important;
  box-shadow: none;
}

.promotion-card.grid-mode::before {
  border-radius: 0;
}

.promotion-card.grid-mode .card-image-wrapper {
  border-radius: 0;
}

.promotion-card.grid-mode .item-count-badge {
  border-radius: 0;
}

.promotion-card.grid-mode .view-details {
  border-radius: 0;
}

.promotion-card.grid-mode:hover {
  transform: none;
  box-shadow: none;
  border-color: var(--border-color);
  background: var(--bg-secondary);
  border-radius: 0 !important;
}

.promotion-card.grid-mode:active {
  transform: none;
}
</style>
